import { isbot } from 'isbot';

import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';
import { getLocalStorage } from '@/utils/localstorage';

import { Cookie } from '../../utils/cookie';

import { AnalyticsState } from './analytics-state';
import { ATL_COHORT, Cohort } from './cohort';
import { AnalyticsData, GASv3 } from './gasv3';
import { Gtm } from './gtm-integration';
import { Guid } from './guid';
import { Helpers } from './helpers';
import { Identity } from './identity';

type TrackEventProperties = {
	actionSubjectId?: string;
	eventContainer?: string;
	eventColumn?: string;
	eventComponent?: string;
	href: string;
	uuid?: string;
	event?: string;
	inputValue?: string;
	parentLabel?: string;
	label: string;
	labelEnglish?: string;
	productKey?: string;
	productVersion?: string;
	productEdition?: string;
	productPlatform?: string;
	schemaVersion?: string;
	name?: string;
	form?: string;
};

export class Tracking {
	public static bxpGatewayRequestId: string | null;
	public static pageViewId: string | null;
	public static xid?: string | number | null;

	public static _getEventDomain() {
		return location && location.href ? new URL(location.href).hostname : '';
	}
	public static _browserSupportsPromises() {
		return typeof Promise === 'function';
	}

	public static _userOptedOutOfTrackingCookies() {
		return window.OnetrustActiveGroups && !/,4,/.exec(window.OnetrustActiveGroups);
	}

	public static _shouldUsePromise() {
		return Tracking._browserSupportsPromises() && !Tracking._userOptedOutOfTrackingCookies();
	}

	/**
	 * Send a track/UI event
	 * @method trackEvent
	 * @param {string} event the name of the event
	 * @param {object} data the properties to be sent with the event
	 */
	public static trackEvent(event: string, data?: AnalyticsData) {
		if (Tracking._shouldUsePromise()) {
			void Tracking.enrichEventDataPromise(data)
				.then((enrichedData) => Tracking._trackEvent(event, enrichedData))
				.catch((e) => {
					console.warn('enrich Event + tracking._trackEvent invocation failed', e);
				});
		} else {
			try {
				data = Tracking.enrichEventData(data);
				Tracking._trackEvent(event, data);
			} catch (e) {
				console.warn('enrich Event + tracking._trackEvent invocation failed', e);
			}
		}
	}

	public static _trackEvent(event: string, enrichedData: AnalyticsData) {
		GASv3.sendUIEvent(event, enrichedData);
		Gtm.track(enrichedData, event);
	}

	/**
	 * send a page/screen event
	 * @function pageView
	 * @param {object} pageViewProperties - optional, dictionary of properties of the page (see: https://segment.com/docs/libraries/analytics.js/#page)
	 * @param {object} pageViewOptions - optional, dictionary options to enable or disable specific integrations for the call (see: https://segment.com/docs/libraries/analytics.js/#page)
	 * */
	public static pageView(pageViewProperties: AnalyticsData) {
		if (Tracking._shouldUsePromise()) {
			void Tracking.enrichEventDataPromise(pageViewProperties)
				.then(function (enrichedPageViewProperties) {
					Tracking._pageView(enrichedPageViewProperties);
				})
				.catch((e) => {
					console.warn('enrich Event + tracking._pageView invocation failed', e);
				});
		} else {
			try {
				pageViewProperties = Tracking.enrichEventData(pageViewProperties);
				Tracking._pageView(pageViewProperties);
			} catch (e) {
				console.warn('enrich Event + tracking._pageView invocation failed', e);
			}
		}
	}

	private static _pageView(enrichedPageViewProperties: AnalyticsData) {
		enrichedPageViewProperties.wrapperName = 'atl-global';
		enrichedPageViewProperties.destinationFlag = 'gasv3';
		enrichedPageViewProperties.isBot = isbot(navigator.userAgent);

		if (!!window.cmsPageVariant) {
			enrichedPageViewProperties.cmsPageVariant = window.cmsPageVariant;
		}
		if (!!window.isUniform && window.compositionStatusCode !== 404) {
			enrichedPageViewProperties.isUniform = window.isUniform;
		}

		// AJ attributes — surfaced as top-level page view properties so they
		// are available on the page event itself (not nested inside
		// atl_first_visit).
		if (!!window.channel) {
			enrichedPageViewProperties.page_channel = window.channel;
		}
		if (!!window.anchor) {
			enrichedPageViewProperties.page_anchor = window.anchor;
		}
		if (!!window.subAnchor) {
			enrichedPageViewProperties.page_subAnchor = window.subAnchor;
		}
		if (!!window.keyword) {
			enrichedPageViewProperties.page_aj_keyword = window.keyword;
		}

		const isEnableOperationalEventWhenOptedOut = checkFeatureFlagGate(
			featureFlags.gwp10416DisableNonOperationalAnalyticsGateUip,
		);
		if (isEnableOperationalEventWhenOptedOut) {
			enrichedPageViewProperties.sn_id = AnalyticsState.snId ?? '';
			enrichedPageViewProperties.atl_first_visit = AnalyticsState.atlFirstVisit ?? '{}';

			enrichedPageViewProperties.event = 'page';
			enrichedPageViewProperties.eventComponent = 'requested';

			GASv3.sendOperationalEvent(enrichedPageViewProperties);
		}

		if (AnalyticsState.analyticsOptOut) {
			return;
		}
		Gtm.page(null, null, enrichedPageViewProperties);
		GASv3.sendScreenEvent(null, enrichedPageViewProperties);
	}

	/**
	 * Gets the pageViewId. Creates one if it doesn't exist
	 * @function getPageViewId()
	 */
	public static getPageViewId() {
		if (!Tracking.pageViewId) {
			Tracking.pageViewId = Guid.guid();
		}
		return Tracking.pageViewId;
	}

	/**
	 * adds common data attributes to all track/UI and page/screen events
	 * @function enrichEventData
	 * @param {object} data the data to enrich
	 */
	public static enrichEventDataPromise(data?: AnalyticsData) {
		// If the data contains a href, then we have clicked a link and are navigating away
		// to a different page, so we don't have time to wait for the retry logic.
		const enableRetryForMarketoId = !data?.href && !data?.form;

		return new Promise<AnalyticsData>((resolve) => {
			void Identity.getMarketoIdPromise(enableRetryForMarketoId).then((marketoId) => {
				resolve(Tracking._enrichEventData(data, marketoId));
			});
		});
	}

	/**
	 * adds common data attributes to all track/UI and page/screen events
	 * @function enrichEventData
	 * @param {object} data the data to enrich
	 */
	public static enrichEventData(data?: AnalyticsData) {
		return Tracking._enrichEventData(data, Identity.getMarketoId());
	}

	private static _enrichEventData(inputData: unknown, marketoId: string | null) {
		let data: AnalyticsData = {};

		if (typeof inputData !== 'object' || inputData === null) {
			const dataValue = inputData;
			if (typeof dataValue !== 'undefined') {
				data['data'] = dataValue;
			}
		} else {
			data = inputData as AnalyticsData;
		}

		// if not already done, read the request Id cookie set by bxp-gateway
		if (!Tracking.bxpGatewayRequestId) {
			Tracking.bxpGatewayRequestId = Cookie.readCookie('bxp_gateway_request_id');
		}
		// Only set the property if the cookie was there and readCookie didn't return null
		if (Tracking.bxpGatewayRequestId) {
			data['bxpGatewayRequestId'] = Tracking.bxpGatewayRequestId;
		}

		data['pageViewId'] = Tracking.getPageViewId();

		// add identity data, if not present
		data['atlEventId'] = Guid.guid();
		data['atlJSVersion'] = AnalyticsState.version || '1.0.0';
		data['browserLanguages'] = navigator.languages;

		// add category, if not present
		data['category'] =
			data['category'] ||
			(document.title !== 'undefined' ? document.title.trim().replace(/\W+/g, '_') : '');
		// add the cohort to the object
		data[ATL_COHORT] = Cohort.readWriteAtlCohort();

		const utms = AnalyticsState.utms;
		if (!!utms) {
			data['campaign'] = {
				name: utms['utm_campaign'],
				campaign: utms['utm_campaign'],
				'sfdc-campaign_id': utms['utm_sfdc-campaign_id'],
				medium: utms['utm_medium'],
				source: utms['utm_source'],
				content: utms['utm_content'],
				term: utms['utm_term'],
			};
		}

		data['clientSessionId'] = Tracking.readWriteSessionCookie();

		if (Tracking.xid) {
			data['atlXid'] = Tracking.xid;
		}

		data['marketoId'] = marketoId ? marketoId : '';
		data['onboardingJourney'] = JSON.parse(getLocalStorage('onboarding-journey') || '""');
		return data;
	}

	/**
	 * @function buildTrackEventProperties
	 * @param {HTMLElement} el target element
	 * @param {object} dataset takes data set from event.target.dataset
	 * @param {string} label
	 * @param {string} href
	 * @returns {object} trackEventProperties
	 */
	public static buildTrackEventProperties(
		el: HTMLElement | null,
		dataset: DOMStringMap,
		label: string,
		href: string,
	) {
		if (!dataset) {
			return;
		}

		const trackEventProperties: TrackEventProperties & {
			dataSet?: Record<string, string>[];
		} = {
			label: label,
			href: href,
		};

		if (el && el.nodeName === 'BUTTON') {
			const form = el.getAttribute('form');
			if (form) {
				trackEventProperties.form = form;
			}
		}

		const expectedProperties: (keyof TrackEventProperties)[] = [
			'actionSubjectId',
			'eventContainer',
			'eventColumn',
			'eventComponent',
			'uuid',
			'event',
			'inputValue',
			'parentLabel',
			'label',
			'labelEnglish',
			'productKey',
			'productVersion',
			'productEdition',
			'productPlatform',
			'schemaVersion',
			'name',
		];

		const isExpectedProperty = (value: string): value is keyof TrackEventProperties =>
			(expectedProperties as string[]).includes(value);

		const dataSetEntries = [];
		for (const dataProperty in dataset) {
			if (dataProperty !== 'track-event' && dataset.hasOwnProperty(dataProperty)) {
				const dataPropertyValue = dataset[dataProperty];
				if (dataPropertyValue) {
					if (isExpectedProperty(dataProperty)) {
						trackEventProperties[dataProperty] = dataPropertyValue;
					} else {
						const dataEntry: Record<string, string> = {};
						dataEntry[dataProperty] = dataPropertyValue;
						dataSetEntries.push(dataEntry);
					}
				}
			}
		}
		trackEventProperties.dataSet = dataSetEntries;
		return trackEventProperties;
	}

	/**
	 * callback for auto track click events
	 * @function _autoTrackClickEvent
	 * @ignore
	 */
	// eslint-disable-next-line complexity
	public static _autoTrackClickEvent(event: Event) {
		try {
			const currentTargetElem = event.currentTarget as HTMLElement;
			let targetElem = event.target as HTMLElement;
			let rawText = targetElem.textContent;

			const isInput = (elem: HTMLElement): elem is HTMLInputElement => elem.tagName === 'INPUT';
			const isAnchor = (elem: HTMLElement): elem is HTMLAnchorElement => elem.hasAttribute('href');
			const isSelect = (elem: HTMLElement): elem is HTMLSelectElement => elem.tagName === 'SELECT';

			// handle form buttons differently
			if (isInput(targetElem)) {
				// targetElem.value of an input text field is considered UGC and should not be tracked
				rawText =
					targetElem.type === 'text'
						? '[REDACTED]'
						: targetElem.value !== ''
							? targetElem.value
							: targetElem.textContent;
			} else if (isSelect(targetElem)) {
				targetElem = targetElem.options[targetElem.selectedIndex] || targetElem;
				rawText =
					targetElem.getAttribute('data-label') ||
					targetElem.textContent ||
					targetElem.ariaLabel ||
					targetElem.className;
			} else if (rawText === '') {
				rawText =
					targetElem.parentNode?.textContent ||
					targetElem.title ||
					targetElem.ariaLabel ||
					targetElem.className;
			}

			const href = isAnchor(targetElem)
				? targetElem.href
				: isAnchor(currentTargetElem)
					? currentTargetElem.href
					: '';
			const trackEventName =
				targetElem.getAttribute('data-track-event') ||
				currentTargetElem.getAttribute('data-track-event') ||
				'Clicked-Auto-CTA';

			const eventLabel = rawText ? rawText.toLowerCase().trim().replace(/ /g, '-') : '';
			const trackEventProperties = Tracking.buildTrackEventProperties(
				currentTargetElem,
				currentTargetElem.dataset,
				eventLabel,
				href,
			);
			//@ts-ignore
			Tracking.trackEvent(trackEventName, trackEventProperties);
		} catch (err) {
			Helpers.sendWrapperLoadError('Error auto-tracking click event');
		}
	}

	/**
	 * queries the document for all selects and adds a change handler to them for tracking
	 * @function autoTrackSelects
	 */
	public static autoTrackSelects() {
		try {
			const selects = Array.from(document.querySelectorAll('select'));
			for (const select of selects) {
				if (!Tracking.isAutoTrackingDisabled(select)) {
					select.removeEventListener('change', Tracking._autoTrackClickEvent);

					select.addEventListener('change', Tracking._autoTrackClickEvent);
				}
			}
		} catch (e) {
			Helpers.sendWrapperLoadError('Error auto-tracking select event');
		}
	}

	/**
	 * queries the document for all submit buttons and adds a click handler to them for tracking
	 * @function autoTrackButtons
	 * @param {boolean} trackAll Indicates whether to use WAC's extended auto-tracking
	 */
	public static autoTrackButtons(trackAll?: boolean) {
		try {
			const selector = trackAll
				? "button, input[type='submit'], input[type='button']"
				: "button, input[type='submit']";
			const btns = Array.from(document.querySelectorAll(selector));
			for (const btn of btns) {
				if (!Tracking.isAutoTrackingDisabled(btn)) {
					// remove existing event listener (to prevent redundant listeners from useEffect hooks)
					btn.removeEventListener('click', Tracking._autoTrackClickEvent);
					// add an event listener
					btn.addEventListener('click', Tracking._autoTrackClickEvent);
				}
			}
		} catch (e) {
			Helpers.sendWrapperLoadError('Error auto-tracking button event');
		}
	}

	/**
	 * queries the document for all links with a class of 'button', 'btn', 'cta' and 'external-link' and adds click handler for tracking
	 * @function autoTrackLinks
	 * @param {boolean} trackAll Indicates whether to use WAC's extended auto-tracking
	 */
	public static autoTrackLinks(trackAll?: boolean) {
		try {
			const links = [],
				selector = trackAll
					? 'a, .external-link'
					: "a[class*='button'], a[class*='btn'], .external-link",
				btnyLinks = Array.from(document.querySelectorAll(selector));

			for (const el of btnyLinks) {
				const htmlEl = el as HTMLElement;
				if (
					trackAll ||
					htmlEl.offsetWidth >= 50 ||
					el.className.includes('cta') ||
					el.className.includes('external-link')
				) {
					links.push(el);
				}
			}

			for (const link of links) {
				if (!Tracking.isAutoTrackingDisabled(link)) {
					link.removeEventListener('click', Tracking._autoTrackClickEvent);
					link.addEventListener('click', Tracking._autoTrackClickEvent);
				}
			}
		} catch (e) {
			Helpers.sendWrapperLoadError('Error auto-tracking link event');
		}
	}

	/**
	 * callback for auto track video events
	 * @function _autoTrackVideoEvent
	 * @ignore
	 */
	public static _autoTrackVideoEvent(element: Element, eventName: string) {
		try {
			const className =
					typeof element.className !== 'undefined'
						? element.className
						: element.parentElement?.className,
				eventLabel = typeof element.textContent !== 'undefined' ? element.textContent : '',
				eventCategory =
					typeof document.title !== 'undefined' ? document.title.replace(/\W+/g, '_').trim() : '';
			Tracking.trackEvent(eventName, {
				className: className,
				category: eventCategory,
				label: eventLabel,
				_sio: Cookie.readCookie('_sio') || '',
				_atl_path: Cookie.readCookie('__atl_path') || '',
			});
		} catch (e) {
			Helpers.sendWrapperLoadError('Error auto-tracking video event');
		}
	}

	/**
	 * queries the document for all divs with video open and close links and adds a click handler for tracking
	 * @function autoTrackVideos
	 */
	public static autoTrackVideos() {
		try {
			const videoOpenLinks = Array.from(
				document.querySelectorAll(
					"div[class*='videoPlayButton'], .video-link, .video_link, .customer-story-video-link",
				),
			);
			const videoCloseLinks = Array.from(
				document.querySelectorAll("div[class='overlay'], .modal.video .close"),
			);

			const trackVideoOpenEvent = function (ev: Event) {
				const currentTargetElem = ev.currentTarget as Element;
				Tracking._autoTrackVideoEvent(currentTargetElem, 'Opened Video');
			};
			const trackVideoCloseEvent = function (ev: Event) {
				const currentTargetElem = ev.currentTarget as Element;
				Tracking._autoTrackVideoEvent(currentTargetElem, 'Closed Video');
			};

			for (const videoOpenLink of videoOpenLinks) {
				videoOpenLink.removeEventListener('click', trackVideoOpenEvent);
				videoOpenLink.addEventListener('click', trackVideoOpenEvent);
			}
			for (const videoCloseLink of videoCloseLinks) {
				videoCloseLink.removeEventListener('click', trackVideoCloseEvent);
				videoCloseLink.addEventListener('click', trackVideoCloseEvent);
			}
		} catch (e) {
			Helpers.sendWrapperLoadError('Error auto-tracking video event');
		}
	}

	/**
	 * queries the document for all role='tab' and adds a click handler to them for tracking
	 * @function autoTrackTabs
	 */
	public static autoTrackTabs() {
		try {
			const selector = "[role='tab']";
			const tabs = Array.from(document.querySelectorAll(selector));
			for (const tab of tabs) {
				if (!Tracking.isAutoTrackingDisabled(tab)) {
					// remove existing event listener (to prevent redundant listeners from useEffect hooks)
					tab.removeEventListener('select', Tracking._autoTrackClickEvent);
					// add an event listener
					tab.addEventListener('select', Tracking._autoTrackClickEvent);
				}
			}
		} catch (e) {
			Helpers.sendWrapperLoadError('Error auto-tracking tab event');
		}
	}

	/**
	 * queries the document for all details elements and adds a toggle handler to them for tracking
	 * @function autoTrackDetails
	 */
	public static autoTrackDetails() {
		try {
			const details = Array.from(document.querySelectorAll('details'));
			for (const detail of details) {
				if (!Tracking.isAutoTrackingDisabled(detail)) {
					detail.removeEventListener('toggle', Tracking._autoTrackToggleEvent);
					detail.addEventListener('toggle', Tracking._autoTrackToggleEvent);
				}
			}
		} catch (e) {
			Helpers.sendWrapperLoadError('Error auto-tracking details event');
		}
	}

	/**
	 * callback for auto track toggle events on details elements
	 * @function _autoTrackToggleEvent
	 * @ignore
	 */
	public static _autoTrackToggleEvent(event: Event) {
		try {
			const detailsElem = event.currentTarget as HTMLDetailsElement;
			const summaryElem = detailsElem.querySelector('summary');
			const rawText = summaryElem?.textContent || detailsElem.textContent || '';
			const isOpen = detailsElem.open;

			const trackEventName = detailsElem.getAttribute('data-track-event') || 'Toggled-Auto-Details';

			const eventLabel = rawText.toLowerCase().trim().replace(/ /g, '-');
			const trackEventProperties = Tracking.buildTrackEventProperties(
				detailsElem,
				detailsElem.dataset,
				eventLabel,
				'',
			);

			if (trackEventProperties) {
				trackEventProperties.event = isOpen ? 'opened' : 'closed';
			}

			//@ts-ignore
			Tracking.trackEvent(trackEventName, trackEventProperties);
		} catch (err) {
			Helpers.sendWrapperLoadError('Error auto-tracking toggle event');
		}
	}

	/**
	 * check to see if the element in question contains class 'doNotAutoTrack'
	 * @function isAutoTrackingDisabled
	 * @param element
	 */
	public static isAutoTrackingDisabled(element: Element) {
		return element.classList.contains('doNotAutoTrack');
	}

	/**
	 * return an existing session cookie
	 * @function readWriteSessionCookie
	 * @returns {string}
	 */
	public static readWriteSessionCookie() {
		const cookie = Cookie.readCookie('atl_session');

		const name = 'atl_session';
		const value = cookie || Guid.guid();
		const seconds = 30 * 60;

		if (Cookie.writeCookieInSeconds) {
			Cookie.writeCookieInSeconds(name, value, seconds);
		}

		return value;
	}
}
